import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Flask, CheckCircle } from "@phosphor-icons/react";

/**
 * EnvBadge — tiny pill in the app header that tells the operator at a glance
 * whether they are looking at the staging sandbox or at live production.
 *
 * Rationale: now that lodgy.co runs on a dual-environment setup (Emergent
 * preview ↔ VPS prod), the two UIs look identical. Without a visual tell,
 * operators can accidentally publish test data to real OTA inventory. The
 * badge sources its truth from `GET /api/env-info`, which reads
 * `CHANNEX_BASE_URL` on the server — never trust the client to classify
 * itself.
 */
export default function EnvBadge() {
  const [env, setEnv] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/env-info")
      .then((r) => { if (!cancelled) setEnv(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!env) return null;

  // Production: minimal green "LIVE" badge — reassurance, not alarm.
  if (!env.is_staging) {
    return (
      <span
        title={`Produkcija — ${env.channex_base_url}`}
        className="ml-1 hidden items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 sm:inline-flex dark:text-emerald-400"
        data-testid="env-badge-live"
      >
        <CheckCircle size={10} weight="fill" />
        Live
      </span>
    );
  }

  // Staging: louder amber pill with a flask icon so there's no mistaking it.
  return (
    <span
      title={`Staging sandbox — ${env.channex_base_url}. Promjene ne idu na prave OTA kanale.`}
      className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
      data-testid="env-badge-staging"
    >
      <Flask size={10} weight="fill" />
      <span className="hidden sm:inline">Staging</span>
    </span>
  );
}
